import { NavLink, Route, Routes, useLocation, useParams } from 'react-router-dom'
import './public.css'
import GraphicPagePublicDetail from './GraphicPagePublicDetail'
import GraphicPagesPublicList from './GraphicPagesPublicList'
import PublicHomePage from './PublicHomePage'
import PublicItemDetailPage from './PublicItemDetailPage'
import PublicItemListPage from './PublicItemListPage'
import PublicNotFoundPage from './PublicNotFoundPage'
import PublicPublicationDetailPage from './PublicPublicationDetailPage'
import PublicPublicationListPage from './PublicPublicationListPage'
import PublicTermDetailPage from './PublicTermDetailPage'
import PublicTermListPage from './PublicTermListPage'

/**
 * Local Public Website (LiangCollection 1:1 Absorption — Stop Condition
 * §34.7/§34.8/§34.9). Mounted at `/site/:workspaceSlug/:siteSlug/*`,
 * completely outside `RequireAuth`/`WorkspaceLayout` — a signed-out
 * visitor (or a Playwright script with no session cookie at all) can
 * open this exact same URL and see exactly what an authenticated admin
 * sees for "本地查看" (this round's rule: both read the same live DB,
 * filtered to status=published + visibility=public — see
 * `public_site.py`'s module docstring for why that is NOT a contradiction
 * of "public site must never read draft data").
 *
 * Nav links mirror Legacy's own `templates/public_site/base.html`
 * (条目/分类/展览/研究) with one deliberate addition — 主题 — because
 * Topic is a real, distinct Legacy capability
 * (`collections_admin:theme_list`) that source-audit confirms was never
 * wired to Legacy's own public nav even though the model/admin exists;
 * see LIANGCOLLECTION_FULL_CAPABILITY_AUDIT.md's Topic Landing note.
 */
export default function PublicSiteApp() {
  const { workspaceSlug = '', siteSlug = '' } = useParams()
  const location = useLocation()
  const base = `/site/${workspaceSlug}/${siteSlug}`

  // Legacy's `object_detail.html` extends the bare root `base.html`
  // (no `<nav>`) while every other public page extends
  // `public_site/base.html` (which has the `pub-nav`) — source-audited
  // this round. Item detail owns the full 100vh immersive viewer-stage
  // itself (its own back-arrow + title overlay), so the global nav must
  // not also render above it (extra height above viewer-stage's 100vh
  // pushes it partly below the fold, which is what caused the reported
  // "blank space above / image not centered" symptom).
  const isItemDetail = new RegExp(`^${base}/items/[^/]+/?$`).test(location.pathname)

  return (
    <div className="pub-root">
      {!isItemDetail && (
        <nav className="pub-nav" aria-label="站点导航">
          <NavLink to={`${base}`} className="pub-nav-brand" end>
            LiangCollection
          </NavLink>
          <div className="pub-nav-links">
            <NavLink to={`${base}/items`} className={({ isActive }) => (isActive ? 'active' : '')}>
              条目
            </NavLink>
            <NavLink to={`${base}/categories`} className={({ isActive }) => (isActive ? 'active' : '')}>
              分类
            </NavLink>
            <NavLink to={`${base}/topics`} className={({ isActive }) => (isActive ? 'active' : '')}>
              主题
            </NavLink>
            <NavLink to={`${base}/exhibitions`} className={({ isActive }) => (isActive ? 'active' : '')}>
              展览
            </NavLink>
            <NavLink to={`${base}/research`} className={({ isActive }) => (isActive ? 'active' : '')}>
              研究
            </NavLink>
            <NavLink to={`${base}/graphic-pages`} className={({ isActive }) => (isActive ? 'active' : '')}>
              图文
            </NavLink>
          </div>
          <span className="pub-nav-spacer" />
        </nav>
      )}

      <Routes>
        <Route index element={<PublicHomePage workspaceSlug={workspaceSlug} siteSlug={siteSlug} />} />
        <Route path="items" element={<PublicItemListPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} />} />
        <Route path="items/:slug" element={<PublicItemDetailPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} />} />
        <Route
          path="categories"
          element={<PublicTermListPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} kind="category" title="条目分类" />}
        />
        <Route
          path="categories/:slug"
          element={<PublicTermDetailPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} kind="category" />}
        />
        <Route
          path="topics"
          element={<PublicTermListPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} kind="topic" title="主题" />}
        />
        <Route
          path="topics/:slug"
          element={<PublicTermDetailPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} kind="topic" />}
        />
        <Route
          path="exhibitions"
          element={<PublicPublicationListPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} kind="exhibition" title="展览" />}
        />
        <Route
          path="exhibitions/:slug"
          element={<PublicPublicationDetailPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} kind="exhibition" />}
        />
        <Route
          path="research"
          element={<PublicPublicationListPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} kind="research" title="研究" />}
        />
        <Route
          path="research/:slug"
          element={<PublicPublicationDetailPage workspaceSlug={workspaceSlug} siteSlug={siteSlug} kind="research" />}
        />
        <Route path="graphic-pages" element={<GraphicPagesPublicList workspaceSlug={workspaceSlug} siteSlug={siteSlug} />} />
        <Route path="graphic-pages/:slug" element={<GraphicPagePublicDetail workspaceSlug={workspaceSlug} siteSlug={siteSlug} />} />
        <Route path="*" element={<PublicNotFoundPage />} />
      </Routes>

      <footer className="pub-footer">© LiangCollection · Powered by Vistoera</footer>
    </div>
  )
}
